import React, { useEffect, useMemo, useState, useRef } from "react";
import { createColumnHelper, SortingState, Table } from "@tanstack/react-table";
import { PaneBody } from "@/components/pane/paneBody.tsx";
import { IColumn, IRow } from "@/domain/interfaces/sfTable.interface.ts";
import { applyFilters, cn } from "@/lib/utils.ts";
import { useGrid } from "@/hooks/useGrid.ts";
import { usePaneContext } from "@/components/pane/paneContext.ts";
import { HighlightedValueChange } from "@/components/shared/highlightedValueChange.tsx";
import { Loader } from "@/components/shared/loader.tsx";
import { PaneSubHeader } from "@/components/pane/paneSubHeader.tsx";
import { useDashboard } from "@/lib/store";

import { ILayering } from "@/domain/interfaces/general.interface";
import { useLayering } from "@/hooks/useLayering";
import { useFilterData } from "@/hooks/useFilterData";
import { isNil } from "lodash";

const columnHelper = createColumnHelper<ILayering>();

const columns = [
  columnHelper.accessor("symbol", {
    header: "Symbol",
    enableSorting: false,
  }),

  columnHelper.accessor("value", {
    header: "Value",
    cell: (row) => {
      const value = row.getValue();
      if (isNil(value)) {
        return <div>-</div>;
      }
      return (
        <HighlightedValueChange
          value={value}
          highlightNewTextColor={false}
          highlightPosNeg={true}
          className="font-bold"
          decimal={2}
        />
      );
    },
    size: 120,
    minSize: 120,
  }),
];

export function LayeringPaneBody() {
  const nodeId = usePaneContext();
  const { node, getNodeConfig } = useGrid(nodeId);
  const config = getNodeConfig();

  const { data: layering, isLoading } = useLayering();
  const filteredData = useFilterData<ILayering>(layering?.data || []);
  const { globalFilters, getGlobalFilterValues, blacklist } = useDashboard();

  const prevSymbolsRef = useRef<Set<string>>(new Set());

  const preparedData = useMemo(() => {
    if (!layering || !filteredData || isLoading) {
      return [];
    }
    filteredData.forEach((item: ILayering) => {
      item.valueFrom = item.value;
      item.valueTo = item.value;
    });

    let preparedData = applyFilters(
      filteredData,
      globalFilters,
      getGlobalFilterValues(),
    );

    if (blacklist.length > 0) {
      blacklist.forEach((symbol) => {
        preparedData = preparedData.filter((item) => item.symbol !== symbol);
      });
    }

    if (config?.filters && config?.filterValues) {
      return applyFilters<ILayering>(
        preparedData,
        config.filters,
        config.filterValues,
      );
    }

    return preparedData;
  }, [
    layering,
    filteredData,
    isLoading,
    globalFilters,
    getGlobalFilterValues,
    blacklist,
    config.filters,
    config.filterValues,
  ]);

  useEffect(() => {
    if (config.isSound && preparedData.length > 0) {
      const currentSymbols = new Set(preparedData.map((item) => item.symbol));
      const prevSymbols = prevSymbolsRef.current;

      // Find new symbols that weren't in the previous data
      const newSymbols = [...currentSymbols].filter(
        (symbol) => !prevSymbols.has(symbol),
      );

      // If there are new symbols, get the one with highest value
      if (newSymbols.length > 0) {
        console.log(newSymbols);
        const newSymbolsData = preparedData
          .filter((item) => newSymbols.includes(item.symbol))
          .sort((a, b) => b.value - a.value);

        if (newSymbolsData.length > 0) {
          const topNewSymbol = newSymbolsData[0].symbol;
          const msg = `Лейринг ${topNewSymbol.split("USDT")[0]}`;
          console.log(msg);
          const utterThis = new SpeechSynthesisUtterance(msg);
          utterThis.voice = window.speechSynthesis
            .getVoices()
            .filter((voice) => voice.lang === "ru-RU")[0];
          utterThis.lang = "ru-RU";
          window.speechSynthesis.speak(utterThis);
        }
      }

      // Update previous symbols reference
      prevSymbolsRef.current = currentSymbols;
    }
  }, [preparedData, config.isSound]);

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <PaneSubHeader />
      <PaneBody
        className="!h-[calc(100%-24px)]"
        tableColumns={columns as IColumn[]}
        data={preparedData}
      />
    </>
  );
}
